import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, isToday, isSameDay } from 'date-fns';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Grid,
  Alert,
  Collapse,
  Badge,
  Tooltip,
  Stack,
  TextField,
  InputAdornment,
  OutlinedInput,
  FormControl,
  InputLabel,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HotelIcon from '@mui/icons-material/Hotel';
import GroupIcon from '@mui/icons-material/Group';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DateRangeIcon from '@mui/icons-material/DateRange';
import NightShelterIcon from '@mui/icons-material/NightShelter';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import useCustomToast from '../hooks/useCustomToast';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import * as XLSX from 'xlsx';
import { formatBookingAmount, getCurrencySymbolFromCode } from '../utils/currencyUtils';

// Token decryption helper
const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

// Styled components
const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 119, 0, 0.05)',
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 2px rgba(255, 119, 0, 0.1)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#FF7700',
      },
    },
  },
}));

const BookingCard = styled(Card)(({ theme, color }) => ({
  position: 'relative',
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    backgroundColor: color,
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px rgba(0,0,0,0.12)`,
  },
}));

const Reservations = ({ initialBookings = [] }) => {
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [tabValue, setTabValue] = useState(0); // 0: Current & Upcoming (check-in >= today), 1: Previous (check-in < today)
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  
  // No Show functionality state
  const [noShowDialogOpen, setNoShowDialogOpen] = useState(false);
  const [selectedBookingForNoShow, setSelectedBookingForNoShow] = useState(null);
  const [noShowLoading, setNoShowLoading] = useState(false);

  // Settlement dialog state
  const [settlementDialogOpen, setSettlementDialogOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementCurrency, setSettlementCurrency] = useState('INR');
  const [commissionExpanded, setCommissionExpanded] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { successToast, errorToast } = useCustomToast();

  useEffect(() => {
    if (initialBookings && initialBookings.length > 0) {
      setBookings(initialBookings);
    }
  }, [initialBookings]);

  useEffect(() => {
    setCommissionExpanded(false);
  }, [selectedSettlement?.settlement_id]);



  // Generate random vibrant colors for borders
  const generateRandomColor = () => {
    const hue = Math.floor(Math.random() * 360);
    return `hsl(${hue}, 80%, 50%)`;
  };
  
  // Create a map of booking IDs to colors (persistent across renders)
  const bookingColors = useMemo(() => {
    const colorMap = {};
    bookings.forEach(booking => {
      if (!colorMap[booking.booking_id]) {
        colorMap[booking.booking_id] = generateRandomColor();
      }
    });
    return colorMap;
  }, [bookings]);

  // Get a darker shade of a given color for hover effects
  const getDarkerShade = (color) => {
    if (!color) return '#888';
    const match = color.match(/\d+/);
    const hue = match ? match[0] : 30;
    return `hsl(${hue}, 85%, 40%)`;
  };

  const formatDate = (dateString) => {
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  /** Date only, no weekday - e.g. "Mar 14, 2026" for settlement dialog */
  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateForSearch = (dateString) => {
    if (!dateString) return '';
    return dateString; // Already in YYYY-MM-DD format from the input
  };

  const calculateStayDuration = (checkInDate, checkOutDate) => {
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const diffTime = Math.abs(checkOut - checkIn);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  /** e.g. country_code "1" + guest_phone "2393219388" → "+1 2393219388" */
  const formatGuestPhone = (booking) => {
    const phone = String(booking?.guest_phone || '').trim();
    if (!phone) return '';
    const code = String(booking?.country_code || '').trim().replace(/^\+/, '');
    return code ? `+${code} ${phone}` : phone;
  };

  const getGuestPhoneParts = (booking) => {
    const phone = String(booking?.guest_phone || '').trim();
    if (!phone) return null;
    const code = String(booking?.country_code || '').trim().replace(/^\+/, '');
    return { code: code || null, phone };
  };

  const renderGuestPhone = (booking) => {
    const parts = getGuestPhoneParts(booking);
    if (!parts) return null;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <PhoneIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />
        {parts.code ? (
          <>
            <span style={{ fontWeight: 700, letterSpacing: '0.02em' }}>+{parts.code}</span>
            <span>{parts.phone}</span>
          </>
        ) : (
          <span>{parts.phone}</span>
        )}
      </span>
    );
  };

  // No Show functionality helpers
  const shouldShowNoShowButton = (booking) => {
    try {
      // Don't show for cancelled or NoShow bookings
      const status = (booking.booking_status || '').toLowerCase();
      if (status === 'noshow' || status === 'cancelled') {
        return false;
      }
      // Only show for pending payment bookings
      if (!booking.payment_status || booking.payment_status.toLowerCase() !== 'pending') {
        return false;
      }
      // Only show for bookings whose checkout date is in the current month and year
      const checkOutDate = new Date(booking.check_out_date);
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      // Show No Show button for bookings from current month that have passed their checkout date
      return (
        checkOutDate.getMonth() === currentMonth &&
        checkOutDate.getFullYear() === currentYear &&
        checkOutDate < now
      );
    } catch (error) {
      console.error('Error checking No Show button visibility:', error);
      return false;
    }
  };

  const handleNoShowClick = (booking) => {
    setSelectedBookingForNoShow(booking);
    setNoShowDialogOpen(true);
  };

  const handleNoShowClose = () => {
    setNoShowDialogOpen(false);
    setSelectedBookingForNoShow(null);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSettlementStatusColor = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'transferred': return 'success';
      case 'captured': return 'info';
      case 'failed': return 'error';
      case 'refunded': return 'warning';
      default: return 'default';
    }
  };

  const getSettlementStatusIcon = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'transferred': return <CheckCircleIcon fontSize="small" />;
      case 'captured': return <PendingIcon fontSize="small" />;
      case 'failed': return <CancelIcon fontSize="small" />;
      default: return <PendingIcon fontSize="small" />;
    }
  };

  const fetchSettlementForBooking = async (bookingId) => {
    const token = getDecryptedToken();
    const hotelId = localStorage.getItem('selectedHotelId');
    if (!token || !hotelId) {
      errorToast('Please log in and select a property');
      return null;
    }
    try {
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/tabtrips_S.php?action=getHotelPaymentSettlements',
        { token, hotel_id: parseInt(hotelId, 10) },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.status && Array.isArray(response.data.data)) {
        setSettlementCurrency(response.data.currency || 'INR');
        const settlement = response.data.data.find(
          (s) => parseInt(s.booking_id, 10) === parseInt(bookingId, 10)
        );
        return settlement || null;
      }
      return null;
    } catch (err) {
      console.error('Error fetching settlement:', err);
      errorToast('Failed to load settlement details');
      return null;
    }
  };

  const handleSettlementClick = async (booking) => {
    if (!booking || !booking.booking_id) return;
    setSettlementDialogOpen(true);
    setSelectedSettlement(null);
    setSettlementLoading(true);
    try {
      const settlement = await fetchSettlementForBooking(booking.booking_id);
      setSelectedSettlement(settlement);
      if (!settlement) {
        errorToast('No settlement found for this booking');
      }
    } finally {
      setSettlementLoading(false);
    }
  };

  const handleSettlementDialogClose = () => {
    setSettlementDialogOpen(false);
    setSelectedSettlement(null);
  };

  const handleNoShowSubmit = async (guestAppeared) => {
    if (!selectedBookingForNoShow) return;
    
    setNoShowLoading(true);
    try {
      const hotelId = localStorage.getItem('selectedHotelId');
      const token = getDecryptedToken();
      
      console.log('Debug info:', {
        hotelId: hotelId,
        selectedBooking: selectedBookingForNoShow,
        localStorageKeys: Object.keys(localStorage)
      });
      
      if (!hotelId) {
        errorToast('Hotel ID not found. Please select a hotel first.');
        return;
      }

      if (!token) {
        errorToast('Authentication token not found. Please login again.');
        return;
      }

      const status = guestAppeared ? 'GuestAppeared' : 'NoShow';
      
      // Send as JSON payload instead of FormData to match your API sample
      const payload = {
        hotel_id: parseInt(hotelId),
        booking_id: selectedBookingForNoShow.booking_id,
        status: status,
        token: token
      };

      console.log('Sending API request:', payload);

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/noShowBookingStatus',
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('API response:', response.data);

      if (response.data.status) {
        // Update the booking in local state
        setBookings(prevBookings => 
          prevBookings.map(booking => 
            booking.booking_id === selectedBookingForNoShow.booking_id
              ? { ...booking, no_show_status: status }
              : booking
          )
        );
        
        successToast(`Booking marked as: ${status === 'NoShow' ? 'No Show' : 'Guest Appeared'}`);
        handleNoShowClose();
      } else {
        errorToast(response.data.message || 'Failed to update booking status');
      }
    } catch (error) {
      console.error('Error updating No Show status:', error);
      errorToast('Failed to update booking status. Please try again.');
    } finally {
      setNoShowLoading(false);
    }
  };

  // Helper: Get bookings for a specific tab with enhanced sorting
  const getBookingsForTab = (targetTab = tabValue) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const filtered = bookings.filter((booking) => {
      const checkInDate = new Date(booking.check_in_date);
      checkInDate.setHours(0, 0, 0, 0);
      return targetTab === 0 ? checkInDate >= today : checkInDate < today;
    });

    // Enhanced sorting logic
    return filtered.sort((a, b) => {
      // Primary sort: Check-in date
      const checkInA = new Date(a.check_in_date);
      const checkInB = new Date(b.check_in_date);
      
      // For upcoming: earliest first, for past: most recent first
      let dateComparison = targetTab === 0 
        ? checkInA - checkInB 
        : checkInB - checkInA;
      
      // If dates are the same, sort by payment status (pending first)
      if (dateComparison === 0) {
        const paymentStatusA = (a.payment_status || '').toLowerCase();
        const paymentStatusB = (b.payment_status || '').toLowerCase();
        
        if (paymentStatusA === 'pending' && paymentStatusB !== 'pending') {
          return -1;
        }
        if (paymentStatusA !== 'pending' && paymentStatusB === 'pending') {
          return 1;
        }
        
        // If payment status is same, sort by booking status (confirmed first, then others)
        if (paymentStatusA === paymentStatusB) {
          const bookingStatusA = (a.booking_status || '').toLowerCase();
          const bookingStatusB = (b.booking_status || '').toLowerCase();
          
          if (bookingStatusA === 'confirmed' && bookingStatusB !== 'confirmed') {
            return -1;
          }
          if (bookingStatusA !== 'confirmed' && bookingStatusB === 'confirmed') {
            return 1;
          }
          
          // If booking status is same, sort by creation date (most recent first)
          if (bookingStatusA === bookingStatusB) {
            const createdA = new Date(a.created_at || 0);
            const createdB = new Date(b.created_at || 0);
            return createdB - createdA;
          }
        }
      }
      
      return dateComparison;
    });
  };

  // Helper: filter bookings by tab and date
  const getFilteredBookings = () => {
    let filtered = getBookingsForTab(tabValue);
    
    // If a date is selected, filter by that date (matches either check-in or check-out date)
    if (selectedDate) {
      const formattedDate = selectedDate.toISOString().split('T')[0];
      filtered = filtered.filter(b => b.check_in_date === formattedDate || b.check_out_date === formattedDate);
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(booking =>
        (booking.guest_name && booking.guest_name.toLowerCase().includes(term)) ||
        formatGuestPhone(booking).includes(term) ||
        (booking.booking_code && booking.booking_code.trim() && booking.booking_code !== '""' && booking.booking_code.toLowerCase().includes(term)) ||
        (booking.booking_id && booking.booking_id.toString().includes(term)) ||
        (booking.guest_email && booking.guest_email.toLowerCase().includes(term))
      );
    }
    
    return filtered;
  };

  const filteredBookings = useMemo(getFilteredBookings, [bookings, tabValue, searchTerm, selectedDate]);

  const getBookingReference = (booking) => {
    if (booking.booking_code && booking.booking_code.trim() && booking.booking_code !== '""') {
      return booking.booking_code;
    }
    return booking.booking_id;
  };

  const formatDateForPdf = (dateString) => {
    if (!dateString) return '-';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return dateString;
    return format(parsed, 'dd MMM yyyy');
  };

  const handleDownloadPdf = () => {
    const dataToExport = getBookingsForTab(tabValue);
    if (dataToExport.length === 0) {
      errorToast(tabValue === 0 ? 'No upcoming bookings to download' : 'No past bookings to download');
      return;
    }

    // Prepare data for Excel
    const excelData = dataToExport.map((booking) => {
      const originalAmount = parseFloat(booking.original_amount || booking.discounted_amount || 0) || 0;
      const commission = parseFloat(booking.tabtrips_commission || 0) || 0;
      const finalAmount = parseFloat(booking.final_amount || booking.discounted_amount || 0) || 0;
      const gstAmount = parseFloat(booking.gst_amount || 0) || 0;
      const finalAmountToPay = parseFloat(booking.final_amount_paid || booking.final_amount || booking.discounted_amount || 0) || 0;
      const currencyCode = booking.currency || 'INR';
      const currencyLabel = getCurrencySymbolFromCode(currencyCode);

      return {
        'Booking': getBookingReference(booking),
        'Guest Name': booking.guest_name || '-',
        'Phone': formatGuestPhone(booking) || '-',
        'Email': booking.guest_email || '-',
        'Check-in Date': formatDateForPdf(booking.check_in_date),
        'Check-out Date': formatDateForPdf(booking.check_out_date),
        'Nights': calculateStayDuration(booking.check_in_date, booking.check_out_date),
        'Guests': booking.total_guests || '-',
        'Booking Status': (booking.booking_status || '-').toUpperCase(),
        'Payment Status': (booking.payment_status || '-').toUpperCase(),
        [`Original Amount (${currencyLabel})`]: formatBookingAmount(originalAmount, currencyCode),
        [`Commission (${currencyLabel})`]: commission > 0 ? formatBookingAmount(commission, currencyCode) : '-',
        [`Final Amount (${currencyLabel})`]: formatBookingAmount(finalAmount, currencyCode),
        [`GST Amount (${currencyLabel})`]: gstAmount > 0 ? formatBookingAmount(gstAmount, currencyCode) : '-',
        [`Final Amount to be Paid (${currencyLabel})`]: formatBookingAmount(finalAmountToPay, currencyCode),
      };
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    ws['!cols'] = [
      { wch: 15 }, // Booking
      { wch: 20 }, // Guest Name
      { wch: 15 }, // Phone
      { wch: 25 }, // Email
      { wch: 15 }, // Check-in Date
      { wch: 15 }, // Check-out Date
      { wch: 8 },  // Nights
      { wch: 8 },  // Guests
      { wch: 15 }, // Booking Status
      { wch: 15 }, // Payment Status
      { wch: 18 }, // Original Amount
      { wch: 15 }, // Commission
      { wch: 15 }, // Final Amount
      { wch: 15 }, // GST Amount
      { wch: 22 }, // Final Amount to be Paid
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Reservations');

    // Generate filename
    const fileSuffix = tabValue === 0 ? 'upcoming' : 'past';
    const fileName = `reservations-${fileSuffix}-${format(new Date(), 'yyyyMMdd-HHmm')}.xlsx`;

    // Write file
    XLSX.writeFile(wb, fileName);
    
    if (successToast) {
      successToast('Downloading reservations Excel file');
    }
  };

  const handleSearchChange = (event) => setSearchTerm(event.target.value);
  const handleDateChange = (date) => setSelectedDate(date);
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDate(null);
  };
  const goToDashboard = () => navigate("/");
  const handleTabChange = (e, v) => setTabValue(v);
  const toggleBookingDetails = (bookingId) => setExpandedBooking(expandedBooking === bookingId ? null : bookingId);

  // Payment status badge style
  const getPaymentStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  // Booking status badge style
  const getBookingStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'confirmed': return 'success';
      case 'noshow': return 'error';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  // Render booking card (mobile-optimized)
  const renderBookingCard = (booking) => {
    const isExpanded = expandedBooking === booking.booking_id;
    const cardColor = bookingColors[booking.booking_id] || generateRandomColor();
    const darkerColor = getDarkerShade(cardColor);
    const bookingCurrency = booking.currency || 'INR';
    
    // Parse room details
    let roomDetails = [];
    try {
      roomDetails = JSON.parse(booking.room_details || '[]');
    } catch (e) {}
    return (
      <BookingCard
        color={cardColor}
        key={booking.booking_id}
        elevation={isExpanded ? 3 : 1}
        sx={{
          mb: 1.2,
          borderRadius: { xs: 2, sm: 2 },
          transition: 'all 0.3s ease',
          border: isExpanded ? `2px solid ${darkerColor}` : `1px solid ${cardColor}`,
          boxShadow: isExpanded ? `0 4px 12px ${cardColor}40` : 'none',
          overflow: 'visible',
          p: { xs: 1.2, sm: 1, md: 0.7 },
          minWidth: 0,
          maxWidth: '100%',
          // Add blurry effect for NoShow bookings
          opacity: booking.booking_status && booking.booking_status.toLowerCase() === 'noshow' ? 0.6 : 1,
          filter: booking.booking_status && booking.booking_status.toLowerCase() === 'noshow' ? 'blur(0.5px)' : 'none',
          backgroundColor: booking.booking_status && booking.booking_status.toLowerCase() === 'noshow' ? '#f5f5f5' : 'inherit',
          borderColor: booking.booking_status && booking.booking_status.toLowerCase() === 'noshow' ? '#ccc' : cardColor,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Stack spacing={{ xs: 0.5, sm: 0.3, md: 0.2 }} sx={{ width: '100%' }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: isExpanded ? `${cardColor}15` : `${cardColor}08`,
                p: { xs: 0.7, sm: 1 },
                px: { xs: 1, sm: 1.5 },
                borderBottom: isExpanded ? `1px solid ${cardColor}40` : 'none',
                minWidth: 0,
              }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Badge color="primary" variant="dot" invisible={!booking.is_new}>
                  <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, fontWeight: 600, minWidth: 0 }}>
                    #{booking.booking_code && booking.booking_code.trim() && booking.booking_code !== '""' ? booking.booking_code : booking.booking_id}
                  </Typography>
                </Badge>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: { xs: '0.7rem', sm: '0.8rem' }, minWidth: 0, whiteSpace: 'nowrap' }}>
                  {formatDate(booking.created_at)}
                </Typography>
              </Stack>
              <Stack 
                direction="row" 
                spacing={{ xs: 0.5, sm: 1 }} 
                alignItems="center"
                sx={{
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end'
                }}
              >
                {/* Booking Status Chip */}
                <Chip
                  label={booking.booking_status}
                  color={getBookingStatusColor(booking.booking_status)}
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.6rem', sm: '0.8rem' },
                    px: { xs: 0.6, sm: 1.5 },
                    py: { xs: 0.3, sm: 0.8 },
                    minWidth: { xs: 35, sm: 80 },
                    height: { xs: 16, sm: 32 },
                    textTransform: 'capitalize',
                    fontFamily: 'inherit',
                    letterSpacing: 0.5,
                    borderRadius: '6px',
                    '& .MuiChip-label': {
                      px: { xs: 0.5, sm: 1 }
                    }
                  }}
                />
                {/* Payment Status Chip */}
                <Chip
                  label={booking.payment_status}
                  color={getPaymentStatusColor(booking.payment_status)}
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.65rem', sm: '1rem' },
                    px: { xs: 0.8, sm: 2 },
                    py: { xs: 0.4, sm: 1 },
                    minWidth: { xs: 40, sm: 100 },
                    height: { xs: 18, sm: 36 },
                    textTransform: 'capitalize',
                    fontFamily: 'inherit',
                    letterSpacing: 0.5,
                    borderRadius: '8px',
                    '& .MuiChip-label': {
                      px: { xs: 0.5, sm: 1 }
                    }
                  }}
                />
              </Stack>
            </Box>
            {/* Content area */}
            <Stack spacing={0.5} sx={{ width: '100%', p: { xs: 0.5, sm: 1 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.1rem' }, mb: 0.2 }}>
                {booking.guest_name}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                {/* Only show phone for current & upcoming and when payment is not Paid */}
                {tabValue === 0 && (booking.payment_status || '').toLowerCase() !== 'paid' && renderGuestPhone(booking)}
                {/* Only show email for current & upcoming and when payment is not Paid */}
                {tabValue === 0 && (booking.payment_status || '').toLowerCase() !== 'paid' && booking.guest_email && booking.guest_email.trim() && (
                  <span><EmailIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} /> {booking.guest_email}</span>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                <span><CalendarTodayIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} /> Check-in {formatDate(booking.check_in_date)}</span>
                <span><AccessTimeIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} /> Check-out {formatDate(booking.check_out_date)}</span>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                <Chip
                  icon={<NightShelterIcon sx={{ fontSize: '0.8rem' }} />}
                  label={`${calculateStayDuration(booking.check_in_date, booking.check_out_date)} nights`}
                  variant="outlined"
                  size="small"
                  sx={{ height: '20px', '& .MuiChip-label': { fontSize: '0.7rem', px: 0.5 } }}
                />
                <Chip
                  icon={<GroupIcon sx={{ fontSize: '0.8rem' }} />}
                  label={`${booking.total_guests} guests`}
                  variant="outlined"
                  size="small"
                  sx={{ height: '20px', '& .MuiChip-label': { fontSize: '0.7rem', px: 0.5 } }}
                />
              </Stack>
              
              {/* Action Buttons Container */}
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 0.5, sm: 1 }, 
                mt: 0.5,
                justifyContent: 'flex-end',
                flexWrap: 'wrap'
              }}>
                {/* Voucher Button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (booking.hotelier_voucher && booking.hotelier_voucher.trim()) {
                      window.open(`https://api.tabtrips.com/olivrweb/hotelapi/${booking.hotelier_voucher}`, '_blank');
                    }
                  }}
                  startIcon={<ReceiptIcon sx={{ fontSize: '0.8rem' }} />}
                  disabled={!booking.hotelier_voucher || !booking.hotelier_voucher.trim()}
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    py: 0.25,
                    px: 1.5,
                    minWidth: { xs: 80, sm: 100 },
                    maxWidth: { xs: 100, sm: 120 },
                    height: { xs: '24px', sm: '28px' },
                    borderRadius: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    borderColor: '#FF7700',
                    color: '#FF7700',
                    opacity: (!booking.hotelier_voucher || !booking.hotelier_voucher.trim()) ? 0.5 : 1,
                    '&:hover': {
                      borderColor: '#E56900',
                      backgroundColor: 'rgba(255,119,0,0.05)',
                    },
                    '&:disabled': {
                      borderColor: '#ccc',
                      color: '#ccc',
                    }
                  }}
                >
                  Voucher
                </Button>
                {(!booking.payment_status || booking.payment_status.toLowerCase() !== 'pending') && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleSettlementClick(booking)}
                    startIcon={<AccountBalanceWalletIcon sx={{ fontSize: '0.8rem' }} />}
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      py: 0.25,
                      px: 1.5,
                      minWidth: { xs: 75, sm: 95 },
                      maxWidth: { xs: 95, sm: 115 },
                      height: { xs: '24px', sm: '28px' },
                      borderRadius: '8px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      borderColor: '#2E7D32',
                      color: '#2E7D32',
                      '&:hover': { borderColor: '#1B5E20', backgroundColor: 'rgba(46, 125, 50, 0.08)' },
                    }}
                  >
                    Settlement
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => toggleBookingDetails(booking.booking_id)}
                  endIcon={isExpanded ? <ExpandLessIcon sx={{ fontSize: '0.9rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}
                  color={isExpanded ? 'primary' : 'info'}
                  sx={{
                    boxShadow: 'none',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    py: 0.25,
                    px: 1.5,
                    minWidth: { xs: 80, sm: 110 },
                    maxWidth: { xs: 110, sm: 140 },
                    height: { xs: '24px', sm: '28px' },
                    borderRadius: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {isExpanded ? 'Less Details' : 'More Details'}
                </Button>
              </Box>
            </Stack>
            {/* Expanded Details */}
            <Collapse in={isExpanded}>
              <Stack spacing={1} sx={{ mt: 1.5, width: '100%' }}>
                <Divider />
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Grid container spacing={1.5} wrap="wrap" sx={{ width: '100%', m: 0 }}>
                    {/* Room Details */}
                    <Grid item xs={12} md={5} sx={{ width: '100%' }}>
                      <Paper variant="outlined" sx={{ p: 1.5, width: '100%' }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                          Room Details
                        </Typography>
                        <Stack spacing={0.5}>
                          {roomDetails.map((room, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <HotelIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: '0.9rem' }} />
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {room.room_type_id} ({room.room_count} room{room.room_count > 1 ? 's' : ''})
                                {room.guests && ` - ${room.guests} guest${room.guests > 1 ? 's' : ''}`}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Paper>
                    </Grid>

                    {/* Payment Details */}
                    <Grid item xs={12} md={7} sx={{ width: '100%' }}>
                      <Paper variant="outlined" sx={{ p: 1.5, width: '100%' }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                          Payment Details
                        </Typography>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Base price
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {booking.pre_promo_room_price != null && parseFloat(booking.pre_promo_room_price) > 0 && (
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', textDecoration: 'line-through', color: 'text.secondary' }}>
                                  {formatBookingAmount(booking.pre_promo_room_price, bookingCurrency)}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                {formatBookingAmount(booking.discounted_amount || booking.total_amount || booking.original_amount || 0, bookingCurrency)}
                              </Typography>
                            </Box>
                          </Box>
                          {booking.gst_amount != null && parseFloat(booking.gst_amount) > 0 && (
                            <>
                              <Divider sx={{ my: 0.5 }} />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  GST
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {booking.pre_promo_gst != null && parseFloat(booking.pre_promo_gst) > 0 && (
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', textDecoration: 'line-through', color: 'text.secondary' }}>
                                      {formatBookingAmount(booking.pre_promo_gst, bookingCurrency)}
                                    </Typography>
                                  )}
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                    {formatBookingAmount(booking.gst_amount, bookingCurrency)}
                                  </Typography>
                                </Box>
                              </Box>
                            </>
                          )}
                          {booking.coupon_details && booking.coupon_details.promo_code && (
                            <>
                              <Divider sx={{ my: 0.5 }} />
                              <Box sx={{ 
                                p: 1, 
                                borderRadius: 1, 
                                backgroundColor: 'rgba(76, 175, 80, 0.08)', 
                                border: '1px solid rgba(76, 175, 80, 0.2)' 
                              }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    Promo discount ({booking.coupon_details.promo_code})
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'success.main' }}>
                                    - {formatBookingAmount(booking.coupon_details.promo_discount_amount || 0, bookingCurrency)}
                                  </Typography>
                                </Box>
                                {booking.coupon_details.promo_borne_by && String(booking.coupon_details.promo_borne_by).toLowerCase() === 'tabtrips' && (
                                  <Typography variant="caption" sx={{ 
                                    display: 'block', 
                                    mt: 0.5, 
                                    color: 'success.dark', 
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                  }}>
                                    Promo funded by Tabtrips
                                  </Typography>
                                )}
                              </Box>
                            </>
                          )}
                          {booking.tabtrips_commission && parseFloat(booking.tabtrips_commission) > 0 && (
                            <>
                              <Divider sx={{ my: 0.5 }} />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  Commission
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#FF7700' }}>
                                  {formatBookingAmount(booking.tabtrips_commission, bookingCurrency)}
                                </Typography>
                              </Box>
                            </>
                          )}
                          <Divider sx={{ my: 0.5 }} />
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: 'rgba(255, 119, 0, 0.06)',
                            border: '1px solid rgba(255, 119, 0, 0.2)'
                          }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                              {booking.payment_status && booking.payment_status.toLowerCase() === 'pending' ? 'Final Amount to be Paid' : 'Final Amount Paid'}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#FF7700' }}>
                              {formatBookingAmount(booking.final_amount_paid || booking.final_amount || 0, bookingCurrency)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                  
                  {/* No Show Button in More Details Section */}
                  {shouldShowNoShowButton(booking) && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => handleNoShowClick(booking)}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          py: 1,
                          px: 3,
                          borderRadius: '8px',
                          borderColor: '#FF7700',
                          color: '#FF7700',
                          '&:hover': {
                            borderColor: '#E56900',
                            backgroundColor: 'rgba(255,119,0,0.05)',
                          }
                        }}
                      >
                        Mark as No Show
                      </Button>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Collapse>
          </Stack>
        </CardContent>
      </BookingCard>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: { xs: 1, sm: 2, md: 3 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 2, sm: 3 },
            mt: { xs: 1, sm: 0 },
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
          }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                color: '#2E4057',
                fontSize: { xs: '1.05rem', sm: '1.5rem', md: '1.75rem' },
                textAlign: 'left',
                flex: 1,
              }}
            >
              Reservations
            </Typography>
            <Tooltip title="View Payment Settlements">
              <Button
                variant="contained"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={() => navigate('/settlements')}
                sx={{
                  backgroundColor: '#FF7700',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 0.75, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  borderRadius: '10px',
                  boxShadow: '0 4px 12px rgba(255,119,0,0.3)',
                  '&:hover': {
                    backgroundColor: '#ff8800',
                    boxShadow: '0 6px 16px rgba(255,119,0,0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Settlements
              </Button>
            </Tooltip>
          </Box>

          {/* Search and Filter Section - form prevents Enter from causing page refresh */}
          <Paper
            component="form"
            noValidate
            onSubmit={(e) => e.preventDefault()}
            elevation={0}
            sx={{
              borderRadius: '14px',
              overflow: 'hidden',
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              mb: { xs: 2, sm: 3 },
              p: { xs: 1, sm: 2, md: 3 },
            }}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'row', md: 'row' },
              gap: { xs: 1, sm: 2 },
              alignItems: 'center',
              mb: 1,
            }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Search by guest name, phone, booking code, or email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                      </InputAdornment>
                    ),
                    sx: {
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      height: { xs: '32px', sm: '40px', md: '48px' },
                      background: '#fff',
                    },
                  }}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ mb: 0 }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <StyledDatePicker
                  label="Filter by date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  inputFormat="dd/MM/yyyy"
                  openTo="day"
                  disableMaskedInput
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '& .MuiInputBase-root': { height: { xs: '32px', sm: '40px', md: '48px' } },
                        background: '#fff',
                      }}
                      onClick={e => {
                        params.inputProps.onClick && params.inputProps.onClick(e);
                        params.inputProps.onFocus && params.inputProps.onFocus(e);
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 1,
            }}>
              <Button
                type="button"
                variant="outlined"
                onClick={clearFilters}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  padding: { xs: '4px 12px', sm: '8px 16px' },
                  minWidth: 0,
                  height: { xs: '28px', sm: '36px' },
                  borderRadius: '8px',
                  fontWeight: 500,
                  letterSpacing: 0.2,
                }}
              >
                Clear
              </Button>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={`${filteredBookings.length} bookings`}
                  color="primary"
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    height: { xs: '24px', sm: '36px' },
                    px: { xs: 1, sm: 2 },
                    borderRadius: '8px',
                    fontWeight: 500,
                  }}
                />
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadPdf}
                  disabled={filteredBookings.length === 0}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    backgroundColor: '#FF7700',
                    '&:hover': {
                      backgroundColor: '#E56900',
                    },
                    '&:disabled': {
                      backgroundColor: '#ccc',
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    padding: { xs: '4px 12px', sm: '8px 16px' },
                    height: { xs: '28px', sm: '36px' },
                  }}
                >
                  {isMobile ? 'Excel' : tabValue === 0 ? 'Download Excel' : 'Download Excel'}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Tabs and Content */}
          <Paper 
            key={`tabs-${tabValue}`} // Force re-render when tab changes
            elevation={0} 
            sx={{
              borderRadius: '14px',
              overflow: 'hidden',
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              mb: 3,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant={isMobile ? 'fullWidth' : 'standard'}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                minHeight: { xs: '40px', sm: '48px' },
                '& .MuiTab-root': {
                  minHeight: { xs: '40px', sm: '48px' },
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '0.95rem' },
                  fontWeight: 600,
                },
              }}
            >
              <Tab icon={<EventAvailableIcon />} iconPosition="start" label={isMobile ? 'Current' : 'Current & Upcoming'} />
              <Tab icon={<EventBusyIcon />} iconPosition="start" label={isMobile ? 'Past Bookings' : 'Past Bookings'} />
            </Tabs>
            <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#FF7700', mb: 2 }} />
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Loading reservations...
                  </Typography>
                </Box>
              ) : filteredBookings.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CalendarTodayIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    No bookings found
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Try adjusting your search filters or date.
                  </Typography>
                </Box>
              ) : (
                <AnimatePresence>
                  {filteredBookings.map((booking, index) => (
                    <motion.div
                      key={booking.booking_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      {renderBookingCard(booking)}
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </Box>
          </Paper>
        </motion.div>
        
        {/* No Show Dialog */}
        <Dialog
          open={noShowDialogOpen}
          onClose={handleNoShowClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid rgba(0,0,0,0.05)',
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            color: '#2E4057',
            fontWeight: 600,
            pb: 1,
            borderBottom: '1px solid rgba(0,0,0,0.08)'
          }}>
            <InfoOutlinedIcon sx={{ color: '#FF7700' }} />
            Guest Attendance Status
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ 
                mb: 1, 
                color: '#2E4057',
                fontWeight: 600,
                fontSize: '1.1rem'
              }}>
                Update Guest Status
              </Typography>
              <Typography variant="body2" sx={{ 
                mb: 2, 
                color: 'text.secondary',
                lineHeight: 1.6
              }}>
                Please confirm the attendance status for this booking:
              </Typography>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'rgba(255,119,0,0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(255,119,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <PersonIcon sx={{ color: '#FF7700', fontSize: '1.2rem' }} />
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: '#2E4057'
                }}>
                  {selectedBookingForNoShow?.guest_name} - Booking #{selectedBookingForNoShow?.booking_code && selectedBookingForNoShow?.booking_code.trim() && selectedBookingForNoShow?.booking_code !== '""' ? selectedBookingForNoShow?.booking_code : selectedBookingForNoShow?.booking_id}
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body2" sx={{ 
              mb: 2,
              color: 'text.secondary',
              fontWeight: 500
            }}>
              Did the guest appear for their booking?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            gap: 2,
            borderTop: '1px solid rgba(0,0,0,0.08)',
            bgcolor: 'rgba(0,0,0,0.02)'
          }}>
            <Button
              onClick={handleNoShowClose}
              disabled={noShowLoading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.05)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleNoShowSubmit(false)}
              disabled={noShowLoading}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#f44336',
                color: '#f44336',
                '&:hover': {
                  borderColor: '#d32f2f',
                  backgroundColor: 'rgba(244,67,54,0.05)',
                }
              }}
            >
              {noShowLoading ? 'Updating...' : 'No Show'}
            </Button>
            <Button
              onClick={() => handleNoShowSubmit(true)}
              disabled={noShowLoading}
              variant="contained"
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#4CAF50',
                '&:hover': {
                  bgcolor: '#388E3C'
                },
                '&:disabled': {
                  bgcolor: 'rgba(76,175,80,0.3)',
                  color: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              {noShowLoading ? 'Updating...' : 'Guest Appeared'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Settlement Details Dialog */}
        <Dialog
          open={settlementDialogOpen}
          onClose={handleSettlementDialogClose}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              maxHeight: isMobile ? '100vh' : '90vh',
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Settlement Details</Typography>
              {selectedSettlement && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedSettlement.booking_code}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleSettlementDialogClose} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: { xs: 2, sm: 2.5 }, px: { xs: 1.5, sm: 3 } }}>
            {settlementLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#FF7700' }} />
              </Box>
            ) : !selectedSettlement ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No settlement found for this booking.
              </Typography>
            ) : (
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 2, minHeight: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', lineHeight: 1.2 }}>Guest</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mt: 0.15, lineHeight: 1.35 }}>{selectedSettlement.guest_name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, fontSize: '0.7rem', lineHeight: 1.35 }}>{selectedSettlement.total_guests} guest(s)</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 2, minHeight: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', lineHeight: 1.2 }}>Dates</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.15, fontSize: '0.8rem', lineHeight: 1.35 }}>Check-in: {formatDateShort(selectedSettlement.check_in_date)}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.35 }}>Check-out: {formatDateShort(selectedSettlement.check_out_date)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, fontSize: '0.7rem' }}>{selectedSettlement.total_nights} night(s)</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 2, height: '100%', minHeight: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <TrendingUpIcon sx={{ color: '#FF7700', fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Settlement Information</Typography>
                    </Box>
                    <Chip
                      icon={getSettlementStatusIcon(selectedSettlement.settlement_status)}
                      label={selectedSettlement.settlement_status || 'Pending'}
                      color={getSettlementStatusColor(selectedSettlement.settlement_status)}
                      size="small"
                      sx={{ fontWeight: 600, height: 24, '& .MuiChip-label': { px: 0.75, fontSize: '0.75rem' } }}
                    />
                    {selectedSettlement.razorpay_transfer_id && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                        Transfer ID: {selectedSettlement.razorpay_transfer_id}
                      </Typography>
                    )}
                    {selectedSettlement.transfer_date && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                        Transfer date: {formatDateTime(selectedSettlement.transfer_date)}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 2, height: '100%', minHeight: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', lineHeight: 1.2 }}>Payment</Typography>
                    <Box sx={{ mt: 0.35 }}>
                      <Chip
                        label={selectedSettlement.payment_status || '—'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          height: 24,
                          '& .MuiChip-label': { px: 0.75, fontSize: '0.75rem' },
                          ...((selectedSettlement.payment_status || '').toLowerCase() === 'paid'
                            ? { bgcolor: '#22c55e', color: '#fff', '&:hover': { bgcolor: '#16a34a' } }
                            : {}),
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.15, fontSize: '0.8rem', lineHeight: 1.35 }}>
                      {(selectedSettlement.payment_method || '').toLowerCase() === 'razorpay' ? 'TabTrips' : (selectedSettlement.payment_method || '—')}
                    </Typography>
                    {selectedSettlement.booked_on && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, fontSize: '0.75rem' }}>Booked: {formatDateTime(selectedSettlement.booked_on)}</Typography>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Rate Breakup</Typography>
                    <Stack divider={<Divider />} spacing={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>(A) Hotel Gross Charges</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Room charges, extra occupancies, hotel taxes</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{settlementCurrency} {parseFloat(selectedSettlement.discounted_amount || 0).toFixed(2)}</Typography>
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, cursor: 'pointer' }} onClick={() => setCommissionExpanded((e) => !e)}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>(B) Commission</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>including GST</Typography>
                            </Box>
                            {commissionExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{settlementCurrency} {parseFloat(selectedSettlement.commission_amount || 0).toFixed(2)}</Typography>
                        </Box>
                        <Collapse in={commissionExpanded}>
                          <Stack spacing={0.5} sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Commission</Typography>
                              <Typography variant="body2">{settlementCurrency} {parseFloat(selectedSettlement.tabtrips_commission || 0).toFixed(2)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">GST @ 18% (includes IGST/CGST/SGST)</Typography>
                              <Typography variant="body2">{settlementCurrency} {parseFloat(selectedSettlement.gst_on_tabtrips_commission || 0).toFixed(2)}</Typography>
                            </Box>
                          </Stack>
                        </Collapse>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>(C) Tax Deductions</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{settlementCurrency} {parseFloat(selectedSettlement.tax_deductions || 0).toFixed(2)}</Typography>
                      </Box>
                      {/* Payment gateway charge hidden for now
                      {selectedSettlement.payment_gateway_charge != null &&
                        selectedSettlement.payment_gateway_charge !== '' &&
                        Number(selectedSettlement.payment_gateway_charge) > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>(D) Payment gateway charge</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {settlementCurrency} {parseFloat(selectedSettlement.payment_gateway_charge || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                      */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, pt: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Payable to Hotel (A-B-C)
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, whiteSpace: 'nowrap', color: '#FF7700' }}>{settlementCurrency} {parseFloat(selectedSettlement.hotel_amount || 0).toFixed(2)}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          {selectedSettlement && !settlementLoading && (
            <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button onClick={handleSettlementDialogClose} sx={{ textTransform: 'none' }}>Close</Button>
            </DialogActions>
          )}
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default Reservations;